import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { api } from "../lib/api";
import { resolveImg } from "./Holiday";

const inputCls = "w-full border border-ink bg-white px-3 py-2 font-serif text-sm focus:outline-none focus:ring-2 focus:ring-ink";
const btnCls = "inline-flex items-center gap-1 border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-bone disabled:opacity-50";
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

function ItemsEditor({ items, onChange, prefix }) {
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2" data-testid={`${prefix}-items`}>
      {items.map((it, i) => (
        <div key={it.id} className="grid grid-cols-12 gap-2 border border-ink/20 bg-bone/50 p-2" data-testid={`${prefix}-item-row-${i}`}>
          <input className={`${inputCls} col-span-12 md:col-span-4`} placeholder="Item name" value={it.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`${prefix}-item-name-${i}`} />
          <input className={`${inputCls} col-span-5 md:col-span-2`} placeholder="$0.00" value={it.price || ""} onChange={(e) => set(i, "price", e.target.value)} data-testid={`${prefix}-item-price-${i}`} />
          <input className={`${inputCls} col-span-7 md:col-span-4`} placeholder="Description (optional)" value={it.desc || ""} onChange={(e) => set(i, "desc", e.target.value)} data-testid={`${prefix}-item-desc-${i}`} />
          <div className="col-span-12 flex justify-end gap-1 md:col-span-2">
            <button type="button" className={btnCls} onClick={() => onChange(move(items, i, -1))} aria-label="Move up"><ArrowUp size={12} /></button>
            <button type="button" className={btnCls} onClick={() => onChange(move(items, i, 1))} aria-label="Move down"><ArrowDown size={12} /></button>
            <button type="button" className={`${btnCls} border-red-700 text-red-700`} onClick={() => onChange(items.filter((_, idx) => idx !== i))} data-testid={`${prefix}-item-remove-${i}`} aria-label="Remove"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      <button type="button" className={btnCls} onClick={() => onChange([...items, { id: newId(), name: "", price: "", desc: "" }])} data-testid={`${prefix}-add-item`}>
        <Plus size={12} /> Add item
      </button>
    </div>
  );
}

function CategoryCard({ cat, index, total, onMove, onReload }) {
  const [form, setForm] = useState(cat);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(cat), [cat]);
  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const body = { ...form, ...patch };
      await api.put(`/owner/menu/categories/${cat.id}`, { name: body.name, note: body.note, hidden: body.hidden, items: body.items.filter((it) => it.name.trim()) });
      toast.success(`Saved "${body.name}"`);
      onReload();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete category "${cat.name}" and all its items?`)) return;
    await api.delete(`/owner/menu/categories/${cat.id}`);
    toast.success("Category deleted");
    onReload();
  };
  const slug = `cat-${index}`;
  return (
    <div className={`border-2 border-ink bg-white ${cat.hidden ? "opacity-60" : ""}`} data-testid={`menu-admin-category-${index}`}>
      <div className="flex flex-wrap items-center gap-2 p-3">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 text-left" data-testid={`${slug}-toggle`}>
          <span className="font-alfa text-lg uppercase">{cat.name}</span>
          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">{cat.items.length} items{cat.hidden ? " · hidden" : ""}</span>
        </button>
        <button type="button" className={btnCls} disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`${slug}-up`}><ArrowUp size={12} /></button>
        <button type="button" className={btnCls} disabled={index === total - 1} onClick={() => onMove(index, 1)} data-testid={`${slug}-down`}><ArrowDown size={12} /></button>
        <button type="button" className={btnCls} onClick={() => save({ hidden: !cat.hidden })} data-testid={`${slug}-visibility`}>
          {cat.hidden ? <><EyeOff size={12} /> Hidden</> : <><Eye size={12} /> Visible</>}
        </button>
        <button type="button" className={`${btnCls} border-red-700 text-red-700`} onClick={remove} data-testid={`${slug}-delete`}><Trash2 size={12} /></button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-ink p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Category name" data-testid={`${slug}-name`} />
            <input className={inputCls} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (e.g. Served with 2 sides)" data-testid={`${slug}-note`} />
          </div>
          <ItemsEditor items={form.items} onChange={(items) => setForm({ ...form, items })} prefix={slug} />
          <button type="button" className={`${btnCls} bg-ink text-bone`} disabled={busy} onClick={() => save()} data-testid={`${slug}-save`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save category
          </button>
        </div>
      )}
    </div>
  );
}

function MenuManager() {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const load = () => api.get("/owner/menu").then(({ data }) => setCats(data)).catch(() => toast.error("Couldn't load menu"));
  useEffect(() => { load(); }, []);
  const onMove = async (i, d) => {
    const next = move(cats, i, d);
    setCats(next);
    await api.post("/owner/menu/reorder", { ids: next.map((c) => c.id) });
  };
  const add = async () => {
    if (!newName.trim()) return;
    await api.post("/owner/menu/categories", { name: newName.trim(), note: "", items: [] });
    setNewName("");
    toast.success("Category added");
    load();
  };
  return (
    <div data-testid="menu-manager">
      <h3 className="font-alfa text-2xl uppercase">Menu Manager</h3>
      <p className="mt-1 font-serif text-sm text-ink/70">Changes go live on the Menu &amp; Order page instantly. Tap a category to edit its items.</p>
      <div className="mt-4 flex gap-2">
        <input className={inputCls} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name (e.g. Appetizers)" data-testid="menu-new-category-input" />
        <button type="button" className={`${btnCls} bg-ink text-bone`} onClick={add} data-testid="menu-new-category-btn"><Plus size={12} /> Add</button>
      </div>
      <div className="mt-4 space-y-2">
        {cats.map((c, i) => <CategoryCard key={c.id} cat={c} index={i} total={cats.length} onMove={onMove} onReload={load} />)}
      </div>
    </div>
  );
}

function HolidayManager() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/holiday").then(({ data }) => setH(data)); }, []);
  if (!h) return null;
  const set = (k, v) => setH({ ...h, [k]: v });
  const save = async (next = h) => {
    setBusy(true);
    try {
      const { tax_rate, ...rest } = next;
      const { data } = await api.put("/owner/holiday", { ...rest, items: next.items.filter((it) => it.name.trim()), pickup_dates: next.pickup_dates.map((d) => d.trim()).filter(Boolean) });
      setH(data);
      toast.success("Holiday menu saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/owner/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("images", [...h.images, ...urls]);
      toast.success("Uploaded — hit Save to publish");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };
  const label = (t, el) => <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{t}</span>{el}</label>;
  return (
    <div className="border-2 border-ink bg-white p-4 shadow-hard md:p-6" data-testid="holiday-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-alfa text-2xl uppercase">Holiday Menu CTA</h3>
        <button
          type="button"
          onClick={() => save({ ...h, enabled: !h.enabled })}
          data-testid="holiday-toggle"
          className={`${btnCls} ${h.enabled ? "bg-ink text-bone" : ""}`}
        >
          {h.enabled ? <><Eye size={12} /> Live on site — tap to hide</> : <><EyeOff size={12} /> Hidden — tap to show</>}
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {label("Button label", <input className={inputCls} value={h.button_label} onChange={(e) => set("button_label", e.target.value)} data-testid="holiday-button-label" />)}
        {label("Small label", <input className={inputCls} value={h.eyebrow} onChange={(e) => set("eyebrow", e.target.value)} data-testid="holiday-eyebrow" />)}
        {label("Section title", <input className={inputCls} value={h.title} onChange={(e) => set("title", e.target.value)} data-testid="holiday-title-input" />)}
        <div className="md:col-span-2">{label("Description", <textarea rows={2} className={inputCls} value={h.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="holiday-subtitle" />)}</div>
      </div>
      <div className="mt-4">
        <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Images (up to 4 shown)</span>
        <div className="flex flex-wrap gap-2">
          {h.images.map((u, i) => (
            <div key={u + i} className="relative h-24 w-24 border border-ink" data-testid={`holiday-image-${i}`}>
              <img src={resolveImg(u)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => set("images", h.images.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 bg-ink p-1 text-bone" data-testid={`holiday-image-remove-${i}`} aria-label="Remove image"><X size={12} /></button>
            </div>
          ))}
          <label className={`${btnCls} h-24 w-24 cursor-pointer flex-col justify-center`} data-testid="holiday-image-upload">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
            <input type="file" accept="image/*" multiple hidden onChange={upload} />
          </label>
        </div>
      </div>
      <div className="mt-4" data-testid="holiday-pickup-dates">
        <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Pickup days customers can choose</span>
        <div className="space-y-2">
          {h.pickup_dates.map((d, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputCls} value={d} onChange={(e) => set("pickup_dates", h.pickup_dates.map((x, idx) => (idx === i ? e.target.value : x)))} data-testid={`holiday-pickup-date-${i}`} />
              <button type="button" className={`${btnCls} border-red-700 text-red-700`} onClick={() => set("pickup_dates", h.pickup_dates.filter((_, idx) => idx !== i))} data-testid={`holiday-pickup-date-remove-${i}`} aria-label="Remove day"><Trash2 size={12} /></button>
            </div>
          ))}
          <button type="button" className={btnCls} onClick={() => set("pickup_dates", [...h.pickup_dates, ""])} data-testid="holiday-pickup-date-add"><Plus size={12} /> Add pickup day</button>
        </div>
      </div>
      <div className="mt-4">
        <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Holiday items — customers order &amp; pay for these on /holiday-menu (price must be a number like $85)</span>
        <ItemsEditor items={h.items} onChange={(items) => set("items", items)} prefix="holiday" />
      </div>
      <button type="button" className={`${btnCls} mt-4 bg-ink text-bone`} disabled={busy} onClick={() => save()} data-testid="holiday-save">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save holiday menu
      </button>
    </div>
  );
}

export default function AdminMenuTab() {
  return (
    <div className="space-y-10">
      <HolidayManager />
      <MenuManager />
    </div>
  );
}
